import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from '@/services/api.service';
import { useAuthStore } from '@/store/useAuthStore';

const QUERY_KEY = ['user', 'seenGuides'];

const localKey = (userId, guide) => `guide:seen:${userId}:${guide}`;

const readLocal = (key) => {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

/**
 * Whether the signed-in person has already seen a guide. Kept on the server
 * per user, so it does not come back on another device; the browser's own
 * note is only a fallback for when the server call fails.
 *
 * @param {string} guide - e.g. 'profile.employee'
 * @returns {{ known: boolean, seen: boolean, markSeen: () => void }}
 *   `known` is false while the answer is still loading.
 */
export default function useSeenGuide(guide) {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const userId = user?._id || user?.id || user?.email || 'anon';
  const key = localKey(userId, guide);

  const { data, isSuccess, isError } = useQuery({
    queryKey: [...QUERY_KEY, userId],
    queryFn: API.users.getSeenGuides,
    staleTime: Infinity,
    retry: false,
  });

  const { mutate } = useMutation({
    mutationFn: () => API.users.markGuideSeen(guide),
    onSuccess: (list) => queryClient.setQueryData([...QUERY_KEY, userId], list),
  });

  const markSeen = useCallback(() => {
    try {
      localStorage.setItem(key, '1');
    } catch {
      // private mode: the server keeps it
    }
    queryClient.setQueryData([...QUERY_KEY, userId], (prev) =>
      Array.isArray(prev) && !prev.includes(guide) ? [...prev, guide] : prev
    );
    mutate();
  }, [key, guide, mutate, queryClient, userId]);

  return {
    known: isSuccess || isError,
    // The server decides; the browser's note counts only when it is unreachable.
    seen: isSuccess ? data.includes(guide) : readLocal(key),
    markSeen,
  };
}
